// frontend/src/components/Chatbot.js
import React, { useState, useRef, useEffect } from 'react';
import '../styles/Chatbot.css';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState('chat'); // 'chat' or 'faq'
  const [messages, setMessages] = useState([
    {
      id: 0,
      type: 'bot',
      text: 'Hi! Welcome to ULJAAPP Help Center. How can I help you today?'
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [activeQuestion, setActiveQuestion] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const knowledgeBase = [
    {
      keywords: ['create account', 'sign up', 'register', 'new account', 'signup', 'registration', 'how to register'],
      answer: "To register on ULJAAPP:\n\n1. Email OTP (Recommended): Enter your email → Click 'Get OTP' → Enter the 6-digit code sent to your email → Complete your profile (name, age, location).\n\n2. Quick Social Login: Click 'Continue with Google' → Complete profile after first login.\n\nFor Legal Advisors: Use the Legal Advisor signup with Bar Council Number."
    },
    {
      keywords: ['log in', 'login', 'sign in', 'signin', 'how to login', 'access account', 'enter account', 'how do i login'],
      answer: "There are 2 ways to login to ULJAAPP:\n\n1. Email OTP Login (Recommended): Enter your email → Click 'Get OTP' → Check your email for a 6-digit code → Enter the OTP to login.\n\n2. Google Login: Click 'Continue with Google' → Select your Google account.\n\nFirst-time users will be asked to complete their profile after logging in."
    },
    {
      keywords: ['forgot password', 'reset password', 'password recovery', 'forgotten password', 'cant login', 'lost password', 'password'],
      answer: "ULJAAPP does not use passwords.\n\nYou can login in 2 ways:\n1. Email OTP: Go to login page → Enter your email → Click 'Get OTP' → Enter the 6-digit OTP from your email.\n2. Google: Click 'Continue with Google' and select your account.\n\nNo password reset is needed."
    },
    {
      keywords: ['social media', 'google login', 'facebook login', 'social login', 'login with google', 'login with facebook'],
      answer: "Yes! ULJAAPP supports Google login. Click 'Continue with Google' on the login page for quick access. After first-time Google login, you'll be prompted to complete your profile. Facebook login is not available. ULJAAPP does not use passwords — you can also login with Email OTP."
    },
    {
      keywords: ['otp not received', 'didnt get otp', 'otp issues', 'otp problem', 'otp expired', 'otp not working'],
      answer: "OTP Issues? Try these:\n\n1. OTP not received? Check spam/junk folder → Wait 30 seconds before requesting new OTP → Ensure email address is correct.\n\n2. OTP expired? OTP is valid for 5 minutes only → Request a new OTP.\n\n3. Account blocked? Contact support: support.uljaapp@atozasindia.in\n\n4. Social login not working? Clear browser cache → Try different browser → Ensure popup blockers are disabled."
    },
    {
      keywords: ['search legal', 'legal solution', 'find law', 'search law', 'how to search', 'search for legal'],
      answer: "Go to the 'Search' page from the homepage or navigation menu.\n\n1. Select your Country and State\n2. Choose a Search Level: Public (Simple & Easy), Professional (Advocate & Advisor), or Academic (Scholarly & Detailed)\n3. Enter your legal query\n\nThe AI will provide structured legal guidance for your jurisdiction. On the results page you can also use Deep Results, Generate Complaint Drafts, Download PDF, share, and Suggested Legal Advisors."
    },
    {
      keywords: ['search level', 'search levels', 'public level', 'professional level', 'academic level', 'commonman', 'simple and easy', 'scholarly', 'what level'],
      answer: "On the Search page you can choose 3 Search Levels:\n\n1. Public Level (Simple & Easy): plain-language answers for ordinary people — direct answer first, then simple explanation.\n2. Professional Level (Advocate & Advisor): practice-ready guidance like an advocate advising a client (laws, sections, steps, documents).\n3. Academic Level (Scholarly & Detailed): deeper analysis with doctrine, precedents, and critical notes.\n\nPick the level before searching. You can also use 'Edit Query & Levels' on the results page to change it."
    },
    {
      keywords: ['document reader', 'read document', 'upload document', 'analyze document', 'ai document reader', 'read my document', 'court notice', 'understand notice'],
      answer: "Use AI Document Reader to understand confusing legal papers in simple language.\n\n1. Login to ULJAAPP (required)\n2. Open 'Document Reader' from the navigation/Explore menu or Services\n3. Upload a file (PDF, DOC/DOCX, TXT, or image) — max about 15MB\n4. The AI explains what the document means, urgency/deadlines, and next steps\n\nHelpful for court notices, legal notices, bank notices, and police documents."
    },
    {
      keywords: ['generate document', 'create document', 'legal document', 'generate draft', 'create draft', 'make document', 'draft forms', 'police complaint', 'bail application', 'legal notice', 'affidavit', 'will', 'court draft'],
      answer: "To create a legal draft:\n\n1. Go to 'Create Draft' / 'Generate Legal Documents' from the homepage or navigation\n2. Choose a draft type, for example:\n   • Honorable Court\n   • Police Station Complaint\n   • Department\n   • General Application/Petition\n   • Consumer Complaint\n   • Legal Notice\n   • Agreement/Contract\n   • Bail Application\n   • Affidavit\n   • Will/Testament\n   • Property Documents\n3. Fill the form details → AI generates a customized draft\n\nYou can save drafts and edit them later from My Drafts / profile."
    },
    {
      keywords: ['complaint draft', 'generate complaint', 'generate complaint drafts', 'complaint drafts from results', 'draft from results'],
      answer: "After you get Legal Search results, open the results page and click 'Generate Complaint Drafts'.\n\nThis helps you create a complaint/draft based on your query and the legal summary shown. You can also create other draft types anytime from 'Create Draft' (police, court, bail, notice, will, etc.)."
    },
    {
      keywords: ['download pdf', 'download result', 'print page', 'share result', 'share results', 'whatsapp share', 'download text', 'export result'],
      answer: "On the Legal Search results page you can:\n\n• Download PDF — save the legal case summary as a PDF\n• Download Text — save as a text file\n• Print Page — print the results\n• Share — share via WhatsApp, Telegram, Facebook Messenger, or Email\n\nThese buttons are below the legal summary on the results page."
    },
    {
      keywords: ['deep results', 'alternative analysis', 'detailed analysis', 'what is deep results'],
      answer: "The Deep Results button provides an alternative analysis using advanced AI. It offers a more detailed legal perspective on your query, giving you multiple angles to understand your legal situation better. You'll find it on the search results page."
    },
    {
      keywords: ['legal advisor', 'find advisor', 'find lawyer', 'get advocate', 'contact lawyer', 'legal advisors', 'suggested legal advisors', 'suggested advisors'],
      answer: "You can find legal advisors in these ways:\n\n1. After a Legal Search: click 'Suggested Legal Advisors' on the results page to see advisors based on your location/query. You can also use Google Maps to find nearby advisors.\n2. Advisors page: open 'Advisors' / Legal Advisors from the navigation to browse verified advisors.\n3. Join the network: if you are a legal professional, use 'Become a Legal Advisor' or the 'Join ULJAAPP Global Legal Advisor Network' option on the Advisors page (Apply Now)."
    },
    {
      keywords: ['contribute', 'share knowledge', 'write article', 'add article', 'submit article', 'contribute article'],
      answer: "Click on 'Share Your Legal Knowledge' from the homepage or navigate to the Contribute section. You can write articles, share case studies, and help build a community of legal knowledge."
    },
    {
      keywords: ['save draft', 'my drafts', 'saved documents', 'access drafts', 'where are drafts'],
      answer: "When generating legal documents, click the 'Save Draft' button. Your drafts are stored securely in your account and can be accessed anytime from the 'My Drafts' section in your profile."
    },
    {
      keywords: ['profile', 'update profile', 'edit profile', 'change profile', 'profile update', 'my profile'],
      answer: "After logging in, you can update your profile anytime:\n\nUpdatable Information: Personal details (name, age, location), contact information (phone, email), profile photo. For Legal Advisors: Bar Council Number, specialization, experience.\n\nTo update: Go to your profile section and click 'Edit Profile'."
    },
    {
      keywords: ['delete account', 'remove account', 'close account', 'deactivate account', 'how to delete'],
      answer: "Go to your Profile page by clicking on your name in the top navigation. Scroll down to 'Advanced Settings' and click 'Delete Account'. You'll need to confirm this action. Note: This permanently deletes all your data including saved drafts."
    },
    {
      keywords: ['logout', 'log out', 'sign out', 'signout', 'how to logout', 'exit account', 'leave account'],
      answer: "To logout from ULJAAPP:\n\n1. Click on your profile name or avatar in the top navigation bar\n2. Click the 'Logout' button\n3. Confirm when prompted\n\nOn mobile: Access 'Explore' menu → Click 'Logout'\n\nYour session will end and you'll be redirected to the login page. Your saved drafts and data remain secure and accessible when you log back in."
    },
    {
      keywords: ['contact', 'support', 'help', 'email support', 'contact support', 'get help'],
      answer: "Contact ULJAAPP Support:\n\n📧 General Support: support.uljaapp@atozasindia.in\n📩 Info & OTP Issues: info.uljaapp@atozasindia.in\n💬 Feedback: feedback.uljaapp@atozasindia.in\n🌐 Website: uljaapp.com\n\nWe're here to help!"
    },
    {
      keywords: ['privacy', 'data security', 'is secure', 'is safe', 'data protection', 'is private'],
      answer: "Yes! ULJAAPP follows a privacy-first approach. Your drafts are not made public. We use secure encryption for data storage and transmission. We never share your personal information with third parties without consent."
    },
    {
      keywords: ['free', 'cost', 'price', 'payment', 'is free', 'how much'],
      answer: "Yes, ULJAAPP is free to start and use. Core features like legal search, document generation, and advisor discovery are available at no cost. We're committed to making legal guidance accessible to everyone."
    },
    {
      keywords: ['language', 'translate', 'regional language', 'hindi', 'tamil', 'other language', 'multilingual'],
      answer: "Yes! ULJAAPP has built-in language translation support. You can search and receive results in multiple Indian languages. The platform automatically detects and translates content for better accessibility."
    },
    {
      keywords: ['become advisor', 'join advisor', 'register as advisor', 'advisor registration', 'become legal advisor', 'join advisor network', 'global legal advisor network', 'apply now advisor'],
      answer: "To join the ULJAAPP Global Legal Advisor Network:\n\n1. Go to 'Become a Legal Advisor' from the homepage, Explore menu, or How It Works section\n   OR open the Legal Advisors page and click 'Apply Now' on 'Join ULJAAPP Global Legal Advisor Network'\n2. Login if needed, then complete the advisor registration form (credentials, practice areas, contact info)\n3. After verification, you'll be listed in the advisor network\n\nIf you are already registered as a Legal Advisor, the Apply Now CTA is hidden for your account."
    },
    {
      keywords: ['news', 'legal news', 'court decisions', 'law updates', 'read news', 'latest news'],
      answer: "Click on 'News' from the navigation menu or homepage. You'll find curated legal news, court decisions, law changes, and updates relevant to various jurisdictions."
    },
    {
      keywords: ['feedback', 'suggestion', 'report issue', 'contact us', 'give feedback', 'submit feedback'],
      answer: "Yes, we value your feedback! You can submit feedback through the Feedback form available in the navigation menu or at the bottom of most pages. Let us know about your experience and suggestions."
    },
    {
      keywords: ['error', 'problem', 'not working', 'bug', 'issue', 'troubleshoot'],
      answer: "Try refreshing the page first. If the issue persists, clear your browser cache or try a different browser. You can report technical issues through the Feedback form with details about the error message."
    },
    {
      keywords: ['up to date', 'latest', 'current laws', 'updated content', 'fresh content'],
      answer: "Yes, we continuously update our content to reflect current laws and regulations. However, laws change frequently, so we recommend verifying critical information with a legal professional for your specific situation."
    }
  ];

  const findAnswer = (userInput) => {
    const input = userInput.toLowerCase().trim();
    
    // Score each knowledge base item
    let bestMatch = null;
    let bestScore = 0;
    
    for (let item of knowledgeBase) {
      let score = 0;
      let matchedKeywords = 0;
      
      for (let keyword of item.keywords) {
        if (input.includes(keyword.toLowerCase())) {
          matchedKeywords++;
          // Longer keyword matches get higher scores
          score += keyword.length;
          
          // Exact phrase match gets bonus
          if (input === keyword.toLowerCase()) {
            score += 50;
          }
        }
      }
      
      // Must match at least one keyword
      if (matchedKeywords > 0) {
        // Bonus for matching multiple keywords
        score += matchedKeywords * 10;
        
        if (score > bestScore) {
          bestScore = score;
          bestMatch = item;
        }
      }
    }
    
    if (bestMatch) {
      return bestMatch.answer;
    }
    
    // Default response if no match
    return "I'm sorry, I couldn't find a specific answer to your question. Please try rephrasing or contact us through the Feedback form for personalized assistance. You can also try keywords like 'login', 'search levels', 'document reader', 'complaint drafts', 'download pdf', 'advisor', 'delete account', etc.";
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    // Add user message
    const userMessage = {
      id: messages.length,
      type: 'user',
      text: inputValue
    };

    // Get bot response
    const botResponse = {
      id: messages.length + 1,
      type: 'bot',
      text: findAnswer(inputValue)
    };

    setMessages([...messages, userMessage, botResponse]);
    setInputValue('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const quickQuestions = [
    "How do I login to ULJAAPP?",
    "What are Search Levels?",
    "How do I use Document Reader?",
    "How can I generate legal documents?",
    "How do I download or share results?"
  ];

  const handleQuickQuestion = (question) => {
    setInputValue(question);
  };

  const faqList = [
    {
      id: 1,
      question: "How do I create an account?",
      answer: "To register on ULJAAPP: (1) Email OTP: Enter your email → Click 'Get OTP' → Enter the 6-digit code → Complete your profile. (2) Quick Social Login: Click 'Continue with Google' → Complete profile after first login. For Legal Advisors: Use the Legal Advisor signup with Bar Council Number."
    },
    {
      id: 2,
      question: "How do I log in to my account?",
      answer: "There are 2 ways to login: (1) Email OTP Login: Enter email → Click 'Get OTP' → Enter the 6-digit code. (2) Google Login: Click 'Continue with Google'. First-time users complete their profile after logging in."
    },
    {
      id: 3,
      question: "Do I need a password to login?",
      answer: "No. ULJAAPP does not use passwords. Login with Email OTP (enter email → Get OTP → enter the 6-digit code) or click 'Continue with Google'. No password reset is needed."
    },
    {
      id: 4,
      question: "OTP not received or expired?",
      answer: "Try these: (1) Check spam/junk folder → Wait 30 seconds before requesting new OTP → Ensure email is correct. (2) OTP expires in 5 minutes → Request a new one. (3) Account blocked? Contact support@uljaapp. (4) Clear browser cache or try different browser."
    },
    {
      id: 5,
      question: "Can I log in using social media?",
      answer: "Yes! ULJAAPP supports Google login. Click 'Continue with Google' on the login page for quick access. After first-time Google login, you'll be prompted to complete your profile. Facebook login is not available. ULJAAPP does not use passwords — you can also login with Email OTP."
    },
    {
      id: 6,
      question: "How do I search for legal solutions?",
      answer: "Go to the 'Search' page. Select Country/State, choose a Search Level (Public, Professional, or Academic), then enter your legal query. The AI returns structured guidance for your jurisdiction. On results you can use Deep Results, Generate Complaint Drafts, Download PDF/share, and Suggested Legal Advisors."
    },
    {
      id: 7,
      question: "What are Search Levels (Public / Professional / Academic)?",
      answer: "Public Level: simple everyday answers. Professional Level: practice-ready advocate-style guidance. Academic Level: scholarly detailed analysis. Choose the level on the Search page before searching, or use 'Edit Query & Levels' on the results page."
    },
    {
      id: 8,
      question: "What is the 'Deep Results' feature?",
      answer: "The Deep Results button on the search results page provides an alternative, more detailed AI analysis of your query with additional legal perspectives."
    },
    {
      id: 9,
      question: "How do I find legal advisors?",
      answer: "After a search, click 'Suggested Legal Advisors' on the results page, or open Legal Advisors from the navigation. You can view advisors, use Google Maps for nearby professionals, or join the network via Become a Legal Advisor / Apply Now."
    },
    {
      id: 10,
      question: "Can I contribute my legal knowledge?",
      answer: "Absolutely! Click on 'Share Your Legal Knowledge' from the homepage or navigate to the Contribute section. You can write articles, share case studies, and help build a community of legal knowledge."
    },
    {
      id: 11,
      question: "How do I save my drafts?",
      answer: "When generating legal documents, click the 'Save Draft' button. Your drafts are stored securely in your account and can be accessed anytime from the 'My Drafts' section in your profile."
    },
    {
      id: 12,
      question: "How do I update my profile?",
      answer: "After logging in, go to your profile section and click 'Edit Profile'. You can update: Personal details (name, age, location), contact info (phone, email), profile photo. For Legal Advisors: Bar Council Number, specialization, experience."
    },
    {
      id: 13,
      question: "How do I delete my account?",
      answer: "Go to your Profile page by clicking on your name in the top navigation. Scroll down to 'Account Settings' and click 'Delete Account'. You'll need to confirm this action. Note: This permanently deletes all your data including saved drafts."
    },
    {
      id: 14,
      question: "How do I logout from ULJAAPP?",
      answer: "To logout: Click on your profile name/avatar in the top navigation bar → Click 'Logout' button → Confirm when prompted. On mobile: Access 'Explore' menu → Click 'Logout'. Your data remains secure and accessible when you log back in."
    },
    {
      id: 15,
      question: "Is my data private and secure?",
      answer: "Yes! ULJAAPP follows a privacy-first approach. Your drafts are not made public. We use secure encryption for data storage and transmission. We never share your personal information with third parties without consent."
    },
    {
      id: 16,
      question: "Is ULJAAPP free to use?",
      answer: "Yes, ULJAAPP is free to start and use. Core features like legal search, document generation, and advisor discovery are available at no cost. We're committed to making legal guidance accessible to everyone."
    },
    {
      id: 16,
      question: "Can I use ULJAAPP in my regional language?",
      answer: "Yes! ULJAAPP has built-in language translation support. You can search and receive results in multiple Indian languages. The platform automatically detects and translates content for better accessibility."
    },
    {
      id: 17,
      question: "How do I become a legal advisor on the platform?",
      answer: "Go to 'Become a Legal Advisor' from the homepage/Explore/How It Works, or open Legal Advisors and click Apply Now on 'Join ULJAAPP Global Legal Advisor Network'. Complete the registration form; after verification you'll be listed. The Apply Now CTA is hidden if you are already a Legal Advisor."
    },
    {
      id: 18,
      question: "Where can I read legal news?",
      answer: "Click on 'News' from the navigation menu or homepage. You'll find curated legal news, court decisions, law changes, and updates relevant to various jurisdictions."
    },
    {
      id: 19,
      question: "Can I provide feedback about the app?",
      answer: "Yes, we value your feedback! You can submit feedback through the Feedback form available in the navigation menu or at the bottom of most pages. Let us know about your experience and suggestions."
    },
    {
      id: 20,
      question: "What should I do if I encounter an error?",
      answer: "Try refreshing the page first. If the issue persists, clear your browser cache or try a different browser. You can report technical issues through the Feedback form with details about the error message."
    },
    {
      id: 21,
      question: "Is the legal content up-to-date?",
      answer: "Yes, we continuously update our content to reflect current laws and regulations. However, laws change frequently, so we recommend verifying critical information with a legal professional for your specific situation."
    },
    {
      id: 22,
      question: "What types of legal documents can I create?",
      answer: "From Create Draft you can make: Honorable Court, Police Station Complaint, Department, General Application/Petition, Consumer Complaint, Legal Notice, Agreement/Contract, Bail Application, Affidavit, Will/Testament, and Property Documents. Each draft is customized from the form you fill."
    },
    {
      id: 23,
      question: "Can I edit a document after generating it?",
      answer: "Yes! After generating a document, you can save it as a draft and edit it anytime. You can also download it and make modifications as needed."
    },
    {
      id: 24,
      question: "How accurate is the AI legal guidance?",
      answer: "Our AI uses advanced algorithms trained on legal databases and provides structured guidance. However, it's for informational purposes only and should not replace professional legal advice for critical matters."
    },
    {
      id: 25,
      question: "What jurisdictions does ULJAAPP cover?",
      answer: "ULJAAPP is global — select your country and state on Search. For India, answers use current laws including BNS/BNSS/BSA (from 1 July 2024) instead of old IPC/CrPC/IEA where applicable. Always verify critical details with a professional."
    },
    {
      id: 26,
      question: "Can I download or share search results?",
      answer: "Yes. On the Legal Search results page use Download PDF, Download Text, Print Page, or share via WhatsApp, Telegram, Messenger, or Email. Generated drafts can also be saved/downloaded from your drafts."
    },
    {
      id: 27,
      question: "Can I use ULJAAPP on mobile devices?",
      answer: "Yes! ULJAAPP is fully responsive and works seamlessly on smartphones, tablets, and desktop computers. You can access all features from any device with an internet connection."
    },
    {
      id: 28,
      question: "What is the difference between regular and Deep Results?",
      answer: "Regular results provide quick, structured legal guidance using our primary AI. Deep Results offers a more comprehensive 9-point analysis with alternative perspectives using advanced DeepSeek AI for complex legal queries."
    },
    {
      id: 29,
      question: "How do I verify if a legal advisor is authentic?",
      answer: "Legal advisors listed on ULJAAPP go through a verification process. Check their profile for credentials, practice areas, experience, and contact information. We recommend verifying their bar registration independently."
    },
    {
      id: 30,
      question: "Can I share my generated documents with others?",
      answer: "You can download your documents and share them privately. However, your saved drafts remain private in your account and are not publicly visible to maintain confidentiality."
    },
    {
      id: 31,
      question: "What are the system requirements to use ULJAAPP?",
      answer: "ULJAAPP works on any modern web browser (Chrome, Firefox, Safari, Edge) with an internet connection. No special software installation is required."
    },
    {
      id: 32,
      question: "How can I access my previous searches?",
      answer: "Your search history and results are accessible through your profile. You can review previous queries, revisit legal solutions, and access saved documents anytime when logged in."
    },
    {
      id: 33,
      question: "Does ULJAAPP provide legal representation?",
      answer: "No, ULJAAPP is an information and document generation platform. We help you connect with legal advisors who can provide representation. The platform itself does not offer legal services or representation."
    },
    {
      id: 34,
      question: "Can I submit articles in regional languages?",
      answer: "Yes! When contributing articles, you can write in multiple Indian languages. Our translation support helps make your content accessible to a wider audience across different regions."
    },
    {
      id: 35,
      question: "What should I do if I disagree with the AI's answer?",
      answer: "AI guidance is informational and may not cover all nuances. We recommend using the Deep Results for alternative analysis, consulting our legal advisor directory, or submitting feedback about specific concerns."
    },
    {
      id: 36,
      question: "How often is the legal news section updated?",
      answer: "Our legal news section is updated daily with the latest court decisions, law amendments, legal developments, and important judgments from various jurisdictions."
    },
    {
      id: 37,
      question: "How can I contact ULJAAPP support?",
      answer: "Contact us at: General Support: support.uljaapp@atozasindia.in | Info & OTP Issues: info.uljaapp@atozasindia.in | Feedback: feedback.uljaapp@atozasindia.in | Website: uljaapp.com"
    },
    {
      id: 38,
      question: "What is Document Reader?",
      answer: "AI Document Reader helps you understand confusing legal papers. Login first, then open Document Reader from navigation/Explore or Services, upload PDF/DOC/image (up to ~15MB), and get a plain-language explanation with urgency, deadlines, and next steps."
    },
    {
      id: 39,
      question: "How do I Generate Complaint Drafts from search results?",
      answer: "After running a Legal Search, open the results page and click 'Generate Complaint Drafts'. This creates a complaint/draft based on your query and summary. You can also create other draft types anytime from Create Draft."
    },
    {
      id: 40,
      question: "How do I join the Global Legal Advisor Network?",
      answer: "Open Legal Advisors and click Apply Now on 'Join ULJAAPP Global Legal Advisor Network', or go to Become a Legal Advisor. Login if needed, complete the form, and after verification you'll appear in the advisor network."
    }
  ];

  const toggleQuestion = (id) => {
    setActiveQuestion(activeQuestion === id ? null : id);
  };

  return (
    <>
      {/* Floating chat button - only show when chatbot is closed */}
      {!isOpen && (
        <button 
          className="chatbot-toggle notranslate"
          onClick={() => setIsOpen(true)}
          aria-label="Open chatbot"
          translate="no"
        >
          <span className="chat-icon notranslate" translate="no" aria-hidden="true">⚖️</span>
        </button>
      )}

      {/* Chatbot window */}
      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <button 
              className="chatbot-close notranslate"
              onClick={() => setIsOpen(false)}
              aria-label="Close chatbot"
              translate="no"
            >
              <span className="notranslate" translate="no" aria-hidden="true">✕</span>
            </button>
            <h3>ULJAAPP Help Center</h3>
            <p>Ask me anything about the app</p>
          </div>

          {/* Mode Toggle */}
          <div className="mode-toggle">
            <button 
              className={`mode-btn ${viewMode === 'chat' ? 'active' : ''} notranslate`}
              onClick={() => setViewMode('chat')}
              translate="no"
            >
              <span className="notranslate" translate="no" aria-hidden="true">💬</span> Chat
            </button>
            <button 
              className={`mode-btn ${viewMode === 'faq' ? 'active' : ''} notranslate`}
              onClick={() => setViewMode('faq')}
              translate="no"
            >
              <span className="notranslate" translate="no" aria-hidden="true">📋</span> FAQs
            </button>
          </div>

          {viewMode === 'chat' ? (
            <>
              <div className="chatbot-messages">
            {messages.map((msg) => (
              <div key={msg.id} className={`chatbot-message chatbot-message-${msg.type}`}>
                <div className="chatbot-message-bubble">
                  {msg.text}
                </div>
              </div>
            ))}
                <div ref={messagesEndRef} />
              </div>

              <div className="chatbot-input">
                <input
                  type="text"
                  placeholder="Type your question..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button onClick={handleSendMessage} aria-label="Send message" className="notranslate" translate="no">
                  <span className="notranslate" translate="no" aria-hidden="true">➤</span>
                </button>
              </div>
            </>
          ) : (
            <div className="faq-mode">
              <div className="faq-list">
                {faqList.map((faq) => (
                  <div 
                    key={faq.id} 
                    className={`faq-item ${activeQuestion === faq.id ? 'active' : ''}`}
                  >
                    <button 
                      className="faq-question"
                      onClick={() => toggleQuestion(faq.id)}
                    >
                      <span className="question-text">{faq.question}</span>
                      <span className="question-icon notranslate" translate="no" aria-hidden="true">
                        {activeQuestion === faq.id ? '−' : '+'}
                      </span>
                    </button>
                    {activeQuestion === faq.id && (
                      <div className="faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Chatbot;
